import { createPortal } from "react-dom";
import type { NiFanResult } from "../lib/derive";

function m(v: number): string {
  const a = Math.abs(v), s = v < 0 ? "-" : "";
  if (a >= 1e9) return `${s}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${s}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${s}$${(a / 1e3).toFixed(0)}K`;
  return `${s}$${a.toFixed(0)}`;
}
const pc = (v: number | null, d = 1) => (v == null ? "—" : `${(v * 100).toFixed(d)}%`);

const WEIGHT_META: Record<string, { label: string; sym: string; desc: string }> = {
  alpha: { label: "Level", sym: "α", desc: "responsiveness to recent results" },
  beta:  { label: "Trend", sym: "β", desc: "how fast the growth slope updates" },
  gamma: { label: "Seasonal", sym: "γ", desc: "how fast the seasonal shape updates" },
  phi:   { label: "Damping", sym: "φ", desc: "how much long-range trend is reined in" },
};

function Gauge({ k, val }: { k: string; val: number }) {
  const meta = WEIGHT_META[k];
  const w = Math.max(0, Math.min(1, val)) * 100;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--t1)" }}>
          <span style={{ color: "var(--accent)", marginRight: 6 }}>{meta.sym}</span>{meta.label}
        </span>
        <span style={{ fontSize: 13, fontWeight: 800, color: "var(--accent)", fontFamily: "var(--mono)" }}>{val.toFixed(3)}</span>
      </div>
      <div style={{ height: 9, borderRadius: 6, background: "var(--surface-2)", overflow: "hidden" }}>
        <div style={{ width: `${w}%`, height: "100%", borderRadius: 6,
          background: "linear-gradient(90deg, var(--accent), var(--accent-2))",
          transition: "width .5s var(--ease-out)" }} />
      </div>
      <div style={{ fontSize: 11, color: "var(--t4)", marginTop: 3 }}>{meta.desc}</div>
    </div>
  );
}

function SeasonalChart({ factors }: { factors: Record<string, number> }) {
  const qs = ["Q1", "Q2", "Q3", "Q4"];
  const vals = qs.map((q) => factors[q] ?? 0);
  const maxAbs = Math.max(1, ...vals.map((v) => Math.abs(v)));
  const W = 340, H = 150, pad = 26, y0 = 78, maxH = 50;
  const bw = 44, gap = (W - 2 * pad - 4 * bw) / 3;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }}>
      <line x1={pad - 6} y1={y0} x2={W - pad + 6} y2={y0} stroke="var(--divider)" strokeWidth={1} />
      {vals.map((v, i) => {
        const x = pad + i * (bw + gap);
        const h = (Math.abs(v) / maxAbs) * maxH;
        const up = v >= 0;
        const y = up ? y0 - h : y0;
        const color = up ? "var(--good)" : "var(--warning)";
        return (
          <g key={i}>
            <rect x={x} y={y} width={bw} height={Math.max(2, h)} rx={3} fill={color} opacity={0.88} />
            <text x={x + bw / 2} y={up ? y - 5 : y + h + 13} textAnchor="middle"
              fontSize={11} fontWeight={700} fill={color} fontFamily="var(--mono)">{m(v)}</text>
            <text x={x + bw / 2} y={H - 6} textAnchor="middle" fontSize={12} fontWeight={700} fill="var(--t3)">{qs[i]}</text>
          </g>
        );
      })}
    </svg>
  );
}

function Waterfall({ d }: { d: { level: number; trend: number; seasonal: number; total: number } }) {
  const W = 360, H = 150, padL = 8, padR = 8, top = 18, rowH = 30, gap = 8;
  const cum = [0, d.level, d.level + d.trend, d.level + d.trend + d.seasonal];
  const maxX = Math.max(d.total, ...cum) * 1.02;
  const minX = Math.min(0, ...cum);
  const span = maxX - minX || 1;
  const sx = (v: number) => padL + ((v - minX) / span) * (W - padL - padR);
  const segs = [
    { label: "Level", from: 0, to: d.level, color: "var(--accent)" },
    { label: "+ Trend", from: d.level, to: d.level + d.trend, color: "var(--accent-2)" },
    { label: "+ Seasonal", from: d.level + d.trend, to: d.level + d.trend + d.seasonal,
      color: d.seasonal >= 0 ? "var(--good)" : "var(--warning)" },
  ];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }}>
      {segs.map((s, i) => {
        const x = sx(Math.min(s.from, s.to)), w = Math.max(2, Math.abs(sx(s.to) - sx(s.from)));
        const y = top + i * (rowH + gap);
        return (
          <g key={i}>
            <rect x={x} y={y} width={w} height={rowH} rx={4} fill={s.color} opacity={0.9} />
            <text x={padL + 2} y={y - 4} fontSize={11} fontWeight={700} fill="var(--t2)">{s.label}</text>
            <text x={x + w + 5} y={y + rowH / 2 + 4} fontSize={11} fontWeight={700}
              fill="var(--t1)" fontFamily="var(--mono)">{m(s.to - s.from)}</text>
          </g>
        );
      })}
      {/* total marker */}
      <line x1={sx(d.total)} y1={top - 6} x2={sx(d.total)} y2={H - 18} stroke="var(--t1)" strokeWidth={1.5} strokeDasharray="3 3" />
      <text x={sx(d.total)} y={H - 4} textAnchor="middle" fontSize={12} fontWeight={800}
        fill="var(--t1)" fontFamily="var(--mono)">= {m(d.total)}</text>
    </svg>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ flex: "1 1 0", minWidth: 80, padding: "10px 12px", borderRadius: 10,
      background: "var(--surface-2)", textAlign: "center" }}>
      <div style={{ fontSize: 17, fontWeight: 800, color: "var(--t1)", fontFamily: "var(--mono)" }}>{value}</div>
      <div style={{ fontSize: 10.5, color: "var(--t4)", marginTop: 2, textTransform: "uppercase", letterSpacing: ".04em" }}>{label}</div>
    </div>
  );
}

export default function ExplainModal({ fan, onClose }: { fan: NiFanResult; onClose: () => void }) {
  const ex = fan.explain;
  if (!ex) return null;
  const a = fan.accuracy;
  const d0 = ex.decomposition[0];

  return createPortal(
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(8,20,24,.55)",
        backdropFilter: "blur(3px)", display: "grid", placeItems: "center", padding: 20,
        animation: "xplFade .18s var(--ease-out)" }}>
      <style>{`@keyframes xplFade{from{opacity:0}to{opacity:1}}
        @keyframes xplPop{from{opacity:0;transform:translateY(10px) scale(.98)}to{opacity:1;transform:none}}
        .xpl-card::-webkit-scrollbar{width:9px}
        .xpl-card::-webkit-scrollbar-thumb{background:var(--surface-3);border-radius:8px}`}</style>
      <div className="xpl-card" onClick={(e) => e.stopPropagation()}
        style={{ width: "min(880px, 96vw)", maxHeight: "90vh", overflowY: "auto",
          background: "var(--surface-elev)", borderRadius: "var(--r-card)",
          boxShadow: "var(--sh-pop)", border: "1px solid var(--hairline)",
          animation: "xplPop .22s var(--ease-out)" }}>

        {/* header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
          padding: "18px 22px 14px", borderBottom: "1px solid var(--divider)",
          background: "linear-gradient(135deg, rgba(var(--accent-rgb),.10), transparent)" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: "var(--accent)", textTransform: "uppercase" }}>
              Why this number? · how this forecast is built
            </div>
            <div style={{ fontSize: 19, fontWeight: 800, color: "var(--t1)", marginTop: 3 }}>{fan.companyName}</div>
            <div style={{ fontSize: 12, color: "var(--t4)", marginTop: 2 }}>{fan.model} · {fan.source}</div>
          </div>
          <button onClick={onClose} aria-label="Close"
            style={{ border: "1px solid var(--hairline)", background: "var(--surface-2)", color: "var(--t2)",
              borderRadius: 8, width: 32, height: 32, fontSize: 18, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>

        {/* body grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22, padding: 22 }}>
          {/* weights */}
          <section>
            <h4 style={sectTitle}>Smoothing weights</h4>
            <p style={sectSub}>Each weight (0–1) controls how strongly a component reacts to new data.</p>
            {(["alpha", "beta", "gamma", "phi"] as const).map((k) => (
              <Gauge key={k} k={k} val={ex.params[k]} />
            ))}
          </section>

          {/* seasonal + decomposition */}
          <section style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div>
              <h4 style={sectTitle}>Seasonal signature</h4>
              <p style={sectSub}>Net-income adjustment the model applies to each calendar quarter.</p>
              <SeasonalChart factors={ex.seasonal_factors} />
            </div>
            <div>
              <h4 style={sectTitle}>Next quarter — {d0?.label}</h4>
              <p style={sectSub}>How the headline forecast is assembled, in dollars.</p>
              {d0 && <Waterfall d={d0} />}
            </div>
          </section>
        </div>

        {/* metrics */}
        <div style={{ padding: "0 22px 8px" }}>
          <h4 style={sectTitle}>Accuracy — walk-forward backtest (out-of-sample)</h4>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
            <Metric label="MAPE" value={pc(a.qMAPE)} />
            <Metric label="RMSE" value={a.rmse == null ? "—" : m(a.rmse)} />
            <Metric label="MAE" value={a.mae == null ? "—" : m(a.mae)} />
            <Metric label="MASE" value={a.mase == null ? "—" : a.mase.toFixed(2)} />
            <Metric label="80% PI cov" value={pc(a.pi80Coverage, 0)} />
          </div>
        </div>

        {/* narrative */}
        <div style={{ margin: "8px 22px 22px", padding: "12px 14px", borderRadius: 10,
          background: "var(--surface-2)", borderLeft: "3px solid var(--accent)",
          fontSize: 12.5, lineHeight: 1.5, color: "var(--t2)" }}>
          {ex.narrative}
        </div>
      </div>
    </div>,
    document.body,
  );
}

const sectTitle: React.CSSProperties = { fontSize: 13, fontWeight: 800, color: "var(--t1)", margin: "0 0 2px",
  textTransform: "uppercase", letterSpacing: ".04em" };
const sectSub: React.CSSProperties = { fontSize: 11.5, color: "var(--t4)", margin: "0 0 12px" };
